import '@slite/style'
import React, { Suspense, use, useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import { usePageContext } from 'vike-react/usePageContext'

import { DraftPreviewBar } from '../components/DraftPreviewBar'
import {
  DEFAULT_LOCALE,
  defaultIntl,
  getMessagesFromLocale,
} from '../utils/i18n'
import { loadRudderstack } from './rudderstack'

import './style.css'
import './font-sizes.css'
import './line-heights.css'
import './display.css'

type Messages = Record<string, string>

// In dev, render the default locale from each message's source `defaultMessage`
// (empty bundle → react-intl falls back) so copy edits show immediately without
// bumping the message id. An empty bundle makes every id report as a missing
// translation, so we silence just that error.
const EMPTY_MESSAGES: Messages = {}

const messagesPromiseByLocale = new Map<string, Promise<Messages>>()

const getMessagesPromise = (locale: string): Promise<Messages> => {
  let promise = messagesPromiseByLocale.get(locale)
  if (!promise) {
    promise =
      locale === DEFAULT_LOCALE
        ? Promise.resolve(defaultIntl.messages as Messages)
        : (getMessagesFromLocale(locale) as Promise<Messages>)
    messagesPromiseByLocale.set(locale, promise)
  }
  return promise
}

function WrapperInner({ children }: { children: React.ReactNode }) {
  const pageContext = usePageContext()
  const locale = pageContext.locale || DEFAULT_LOCALE
  const loadedMessages = use(getMessagesPromise(locale))

  const forceSourceMessages = import.meta.env.DEV && locale === DEFAULT_LOCALE
  const messages = forceSourceMessages ? EMPTY_MESSAGES : loadedMessages

  useEffect(() => {
    loadRudderstack()
  }, [])

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={DEFAULT_LOCALE}
      messages={messages}
      onError={
        forceSourceMessages
          ? (error) => {
              if (error.code !== 'MISSING_TRANSLATION') console.error(error)
            }
          : undefined
      }
    >
      {children}
      {pageContext.isSanityDraftPreview && <DraftPreviewBar />}
    </IntlProvider>
  )
}

export default function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={null}>
      <WrapperInner>{children}</WrapperInner>
    </Suspense>
  )
}
